import { useState, useCallback } from 'react';

interface TemplateSnippet {
  name: string;
  trigger: string;
  content: string;
}


const BUILT_IN_TEMPLATES: TemplateSnippet[] = [
  {
    name: 'Prompt do Sistema',
    trigger: 'system',
    content: 'Você é um assistente IA útil. Por favor, forneça respostas precisas e bem fundamentadas.'
  },
  {
    name: 'Revisão de Código',
    trigger: 'review',
    content: 'Por favor, revise o seguinte código e forneça feedback sobre:\n1. Qualidade do código e boas práticas\n2. Possíveis bugs ou problemas\n3. Melhorias de performance\n4. Documentação e legibilidade\n\n```\n[Seu código aqui]\n```'
  },
  {
    name: 'Explicar Código',
    trigger: 'explain',
    content: 'Por favor, explique o seguinte código passo a passo:\n\n```\n[Seu código aqui]\n```\n\nIncluir:\n- O que o código faz\n- Como funciona\n- Quaisquer conceitos ou padrões importantes utilizados'
  },
  {
    name: 'Correção de Bug',
    trigger: 'bug',
    content: 'Tenho um bug no meu código. Aqui está o código problemático:\n\n```\n[Seu código aqui]\n```\n\nO problema é: [descreva o problema]\n\nComportamento esperado: [o que deveria acontecer]\nComportamento atual: [o que está acontecendo]\n\nPor favor, me ajude a corrigir isso.'
  },
  {
    name: 'Otimização',
    trigger: 'optimize',
    content: 'Por favor, me ajude a otimizar este código para melhor performance:\n\n```\n[Seu código aqui]\n```\n\nProblemas de performance atuais:\n- [descreva os problemas]\n\nPor favor, sugira melhorias e explique seu raciocínio.'
  },
  {
    name: 'Documentação',
    trigger: 'docs',
    content: 'Por favor, me ajude a escrever documentação para este código:\n\n```\n[Seu código aqui]\n```\n\nPreciso de:\n- Descrições de funções/classes\n- Explicação de parâmetros\n- Exemplos de uso\n- Descrição de valores de retorno'
  }
];

export const useTemplateSnippets = (
  content: string,
  setContent: (content: string) => void
) => {
  const [suggestions, setSuggestions] = useState<TemplateSnippet[]>([]);

  const handleSlashCommand = useCallback((text: string, cursorPos: number) => {
    const lines = text.substring(0, cursorPos).split('\n');
    const currentLine = lines[lines.length - 1];
    
    if (currentLine.startsWith('/')) {
      const query = currentLine.substring(1).toLowerCase();
      
      if (query === '') {
        setSuggestions(BUILT_IN_TEMPLATES);
      } else {
        const filtered = BUILT_IN_TEMPLATES.filter(template =>
          template.name.toLowerCase().includes(query) ||
          template.trigger.toLowerCase().includes(query)
        );
        setSuggestions(filtered);
      }
    } else {
      setSuggestions([]);
    }
  }, []);

  const insertTemplate = useCallback((template: TemplateSnippet, cursorPos: number) => {
    const lines = content.substring(0, cursorPos).split('\n');
    const currentLineIndex = lines.length - 1;
    const currentLine = lines[currentLineIndex];
    
    // Remove the slash command
    const lineWithoutSlash = currentLine.replace(/\/[^/]*$/, '');
    lines[currentLineIndex] = lineWithoutSlash;
    
    const beforeCursor = lines.join('\n');
    const afterCursor = content.substring(cursorPos);
    
    const newContent = beforeCursor + template.content + afterCursor;
    setContent(newContent);
    setSuggestions([]);
  }, [content, setContent]);

  return {
    suggestions,
    handleSlashCommand,
    insertTemplate
  };
};